import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DeeperNavProvider } from "@/context/deeperNav";
import { BookmarksProvider } from "@/context/BookmarksContext";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import StudySetup from "@/pages/StudySetup";
import StudySession from "@/pages/StudySession";
import History from "@/pages/History";
import PassageHistory from "@/pages/PassageHistory";
import DeeperConversation from "@/pages/DeeperConversation";
import PrayerJournal from "@/pages/PrayerJournal";
import TeachShareGallery from "@/pages/TeachShareGallery";
import VoiceNotes from "@/pages/VoiceNotes";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import PaymentResult from "@/pages/PaymentResult";
import Feedback from "@/pages/Feedback";
import Admin from "@/pages/Admin";
import AdminVoicePreviews from "@/pages/AdminVoicePreviews";
import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import SharedStudy from "@/pages/SharedStudy";
import SharedSection from "@/pages/SharedSection";
import SharedDeeper from "@/pages/SharedDeeper";
import SharedSermon from "@/pages/SharedSermon";
import SharedTeachShare from "@/pages/SharedTeachShare";
import Support from "@/pages/Support";
import Help from "@/pages/Help";
import Resources from "@/pages/Resources";
import BibleSearch from "@/pages/BibleSearch";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Welcome from "@/pages/Welcome";
import Pricing from "@/pages/Pricing";
import DailyNuggets from "@/pages/DailyNuggets";
import RoomsLaunch from "@/pages/RoomsLaunch";
import StudyRoom from "@/pages/StudyRoom";
import Unsubscribe from "@/pages/Unsubscribe";
import { UpgradeNudge } from "@/components/UpgradeNudge";
import { Loader2 } from "lucide-react";
import { captureUtm } from "@/lib/utm";
import { initAnalytics } from "@/lib/analytics";

captureUtm();
initAnalytics();

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07090E]">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07090E]">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/study" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/s/:token" element={<SharedStudy />} />
      <Route path="/sec/:token" element={<SharedSection />} />
      <Route path="/dc/:token" element={<SharedDeeper />} />
      <Route path="/sm/:token" element={<SharedSermon />} />
      <Route path="/t/:token" element={<SharedTeachShare />} />
      <Route path="/support" element={<Support />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/daily" element={<DailyNuggets />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/study" element={<StudySetup />} />
        <Route path="/study/:sessionId" element={<StudySession />} />
        <Route path="/rooms" element={<RoomsLaunch />} />
        <Route path="/rooms/:code" element={<StudyRoom />} />
        <Route path="/history" element={<History />} />
        <Route path="/passages/:key" element={<PassageHistory />} />
        <Route path="/deeper/:id" element={<DeeperConversation />} />
        <Route path="/journal" element={<PrayerJournal />} />
        <Route path="/teach" element={<TeachShareGallery />} />
        <Route path="/sermons" element={<Navigate to="/teach" replace />} />
        <Route path="/voice-notes" element={<VoiceNotes />} />
        <Route path="/search" element={<BibleSearch />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/help" element={<Help />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/feedback" element={<Feedback />} />
      </Route>
      <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentResult /></Protected>} />
      <Route
        path="/admin"
        element={
          <AdminOnly>
            <Admin />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/voices"
        element={
          <AdminOnly>
            <AdminVoicePreviews />
          </AdminOnly>
        }
      />
      <Route path="*" element={<Navigate to="/study" replace />} />
    </Routes>
  );
}

function AppShell() {
  return <AppRoutes />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <DeeperNavProvider>
            <BookmarksProvider>
              <AppShell />
            </BookmarksProvider>
          </DeeperNavProvider>
          <UpgradeNudge />
          <Toaster position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
